/**
 * The ONE settings-scope engine. Generic over the edited value `T`, it owns the
 * scope-state machine (tab / user / workspace-user), resolves the value being
 * edited, and wires the standard actions (Customize / Copy from User / Reset /
 * Remove override / Cancel rollback / Save). Every settings surface — the
 * drawing-tool fields, Indicators, Fibonacci, Chart Properties, Weekly
 * Behaviour — runs on this; each only supplies WHERE its values live and HOW to
 * write / persist / clone them.
 *
 * `levels`:
 *   - "tab"            → per-tab only, no scope dropdown (scope locked to tab)
 *   - "user"           → user only, no dropdown (scope locked to user)
 *   - "workspace-user" → the 2-scope cascade with the dropdown + inherit banner
 *
 * Per light/dark mode is the CALLER's concern: it passes the already
 * mode-resolved `tabValue` / `userValue` and routes `writeTab` / `writeUser` to
 * the active mode's slot. For correct Cancel across both modes a per-mode caller
 * supplies `captureSnapshot` / `restoreSnapshot` (else both-slot value snapshot
 * is the default).
 *
 * Read-time cascade (whether the user value applies to charts with no override)
 * is the consumer's effective selector, NOT here.
 */
import { useEffect, useRef, useState } from "react";
import type { ScopeLevel } from "./registry";
import type { ScopeChrome } from "./SettingsPanel";
import type { MoreAction } from "@/components/MoreActionsMenu/MoreActionsMenu";

export type SettingsScope = Extract<ScopeLevel, "workspace" | "user">;
export type ScopeLevels = "tab" | "user" | "workspace-user";

export interface ScopedSettingsConfig<T, S = unknown> {
  levels: ScopeLevels;
  /** Per-tab (workspace) override slot for the active mode, or null. */
  tabValue: T | null;
  /** User-scope slot (template / default) for the active mode, or null. */
  userValue: T | null;
  /** Built-in default used when neither slot is set. */
  defaultValue: T;
  writeTab: (value: T | null) => void;
  writeUser: (value: T | null) => void;
  /** Persist user scope to the server (Save at User scope). */
  persistUser: (value: T | null) => void | Promise<void>;
  /** Deep clone so a seeded override never shares object refs with its source. */
  clone: (value: T) => T;
  saving?: boolean;
  userLabel?: string;
  workspaceLabel?: string;
  removeOverrideTitle?: string;
  /** Optional custom rollback (e.g. snapshot BOTH light+dark slots, or extra
   *  global fields). Defaults to capturing {tab,user} for the active mode and
   *  restoring them via writeTab/writeUser. */
  captureSnapshot?: () => S;
  restoreSnapshot?: (snap: S) => void;
}

export interface ScopedSettings<T> {
  scope: SettingsScope;
  /** Present only for "workspace-user" (the dropdown). */
  scopeUi?: ScopeChrome;
  current: T;
  isInheriting: boolean;
  saving: boolean;
  patch: (next: T) => void;
  customize: () => void;
  removeOverride: () => void;
  /** Replace the tab override with a clone of the User-scope value. */
  copyFromUser: () => void;
  /** Reset the active scope's value to the built-in default. */
  resetToDefaults: () => void;
  cancel: () => void;
  save: (final?: T) => Promise<void>;
  /** Standard ⋯-menu actions (Reset / Copy from User / Remove override). */
  actions: MoreAction[];
}

export function useScopedSettingsCore<T, S = unknown>(
  cfg: ScopedSettingsConfig<T, S>,
  open: boolean,
): ScopedSettings<T> {
  const userLabel = cfg.userLabel ?? "User defaults";
  // Initial/locked scope: "user"-only locks to user; everything else starts at
  // workspace (the common case) and "tab" stays there with no dropdown.
  const lockedUser = cfg.levels === "user";
  const showDropdown = cfg.levels === "workspace-user";
  const [scope, setScope] = useState<SettingsScope>(
    lockedUser ? "user" : "workspace",
  );

  // Snapshot on open for Cancel rollback; reset dropdown to Workspace.
  const snapshotRef = useRef<{ tab: T | null; user: T | null } | S | null>(null);
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      snapshotRef.current = cfg.captureSnapshot
        ? cfg.captureSnapshot()
        : { tab: cfg.tabValue, user: cfg.userValue };
      if (!lockedUser) setScope("workspace");
    }
    prevOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isInheriting =
    showDropdown && scope === "workspace" && cfg.tabValue === null;
  const inherited = cfg.userValue ?? cfg.defaultValue;
  // For "tab" levels there is no user fallback — read the tab slot (or default).
  const current: T =
    scope === "user"
      ? cfg.userValue ?? cfg.defaultValue
      : showDropdown
        ? cfg.tabValue ?? inherited
        : cfg.tabValue ?? cfg.defaultValue;

  const patch = (next: T) => {
    if (scope === "user") cfg.writeUser(next);
    else cfg.writeTab(next);
  };
  const customize = () => cfg.writeTab(cfg.clone(current));
  const removeOverride = () => cfg.writeTab(null);
  const resetToDefaults = () => patch(cfg.clone(cfg.defaultValue));
  const copyFromUser = () =>
    cfg.writeTab(cfg.clone(cfg.userValue ?? cfg.defaultValue));

  const cancel = () => {
    const snap = snapshotRef.current;
    if (snap == null) return;
    if (cfg.restoreSnapshot) {
      cfg.restoreSnapshot(snap as S);
    } else {
      const v = snap as { tab: T | null; user: T | null };
      cfg.writeTab(v.tab);
      cfg.writeUser(v.user);
    }
  };
  const save = async (final?: T) => {
    if (scope === "user" || lockedUser) {
      await cfg.persistUser(final ?? cfg.userValue ?? null);
    }
    snapshotRef.current = null;
  };

  const actions: MoreAction[] = [];
  if (!isInheriting) {
    actions.push({
      label: "Reset to defaults",
      title: "Reset this scope to the built-in defaults",
      onClick: resetToDefaults,
    });
  }
  if (showDropdown && scope === "workspace" && !isInheriting) {
    actions.push({
      label: `Copy from ${userLabel}`,
      title: `Replace this chart's settings with your saved ${userLabel}`,
      onClick: copyFromUser,
    });
    actions.push({
      label: "Remove workspace override",
      title: cfg.removeOverrideTitle ?? `Revert this chart tab to ${userLabel}`,
      onClick: removeOverride,
    });
  }

  const scopeUi: ScopeChrome | undefined = showDropdown
    ? {
        options: [
          { value: "workspace", label: cfg.workspaceLabel ?? "Workspace" },
          { value: "user", label: userLabel },
        ],
        current: scope,
        onChange: (s) => setScope(s as SettingsScope),
      }
    : undefined;

  return {
    scope,
    scopeUi,
    current,
    isInheriting,
    saving: !!cfg.saving,
    patch,
    customize,
    removeOverride,
    copyFromUser,
    resetToDefaults,
    cancel,
    save,
    actions,
  };
}
